import _ from 'lodash';
import { AnyAction } from 'redux';
import IDistribution from '~Api/IncomeTrust/IDistribution';
import IHistory from '~Api/Investment/IHistory';
import IInvestment from '~Api/Investment/IInvestment';
import IInvestmentTransaction from '~Api/Investment/IInvestmentTransaction';
import IPendingDistribution from '~Api/Investment/IPendingDistribution';
import WorkflowStatusEnum from '~Api/Investment/WorkflowStatusEnum';
import IInvestorAccountInvestment from '~Api/Investor/IInvestorAccountInvestment';
import IInvestorAccountInvestmentTransaction from '~Api/Investor/IInvestorAccountInvestmentTransaction';
import {
    IInvestorAccountInvestmentSetAction,
    IInvestorAccountInvestmentsSetAction,
} from '~Investors/actions';
import InvestorsActionsEnum from '~Investors/ActionsEnum';
import { IDictionary } from '~utilities/IDictionary';
import {
    IIncomeTrustInterestRatesSetAction,
    IInvestmentHideAction,
    IInvestmentHistoriesSetAction,
    IInvestmentIncomeTrustDistributionsSetAction,
    IInvestmentIncomeTrustInvestmentRequestCancelAction,
    IInvestmentIncomeTrustInvestmentRequestProcessAction,
    IInvestmentIncomeTrustInvestmentRequestsPendingSetAction,
    IInvestmentIncomeTrustTransactionInvestorsSetAction,
    IInvestmentIncomeTrustTransactionsSetAction,
    IInvestmentInvestorsSetAction,
    IInvestmentPendingDistributionProcessAction,
    IInvestmentPendingDistributionsSetAction,
    IInvestmentPhotoInProgressSetAction,
    IInvestmentReservationsSetAction,
    IInvestmentSetAction,
    IInvestmentTransactionInvestorsSetAction,
    IInvestmentTransactionSetAction,
    IInvestmentTransactionsSetAction,
    IInvestmentValueSetAction,
    IInvestmentsDashboardSetAction,
    IInvestmentsSearchResultsSetAction,
    IInvestmentsSetAction,
    IInvestorIncomeTrustDivestmentRequestCancelAction,
    IInvestorIncomeTrustDivestmentRequestProcessInProgressSetAction,
    IInvestorIncomeTrustDivestmentRequestSetAction,
    IInvestorIncomeTrustsDivestmentRequestsPendingSetAction,
} from './actions';
import InvestmentsActionsEnum from './ActionsEnum';
import IIncomeTrustTransaction from '~Api/IncomeTrust/IIncomeTrustTransaction';
import IInvestorIncomeTrustTransaction from '~Api/IncomeTrust/IInvestorIncomeTrustTransaction';
import IReservation from '~Api/Investment/IReservation';
import InvestmentSaleTransfersActionsEnum from '~InvestmentSaleTransfers/ActionsEnum';
import {
    IInvestmentSaleTransferSetAction,
    IInvestmentSaleTransfersPaginatedSetAction,
    IInvestmentSaleTransfersUnallocatedInvestmentsSetAction,
} from '~InvestmentSaleTransfers/actions';
import IInvestmentSaleTransfer from '~Api/InvestmentSaleTransfer/IInvestmentSaleTransfer';
import IUnallocatedInvestment from '~Api/InvestmentSaleTransfer/IUnallocatedInvestment';
import IIncomeTrustInvestmentRequest from '~Api/IncomeTrust/IIncomeTrustInvestmentRequest';
import IInvestorIncomeTrustDivestmentRequest from '~Api/IncomeTrust/IInvestorIncomeTrustDivestmentRequest';
import LoansActionsEnum from '~Loans/ActionsEnum';
import { ILoanInvestmentsSetAction } from '~Loans/actions';
import IIncomeTrustInterestRate from '~Api/IncomeTrust/IIncomeTrustInterestRate';
import InvestorIncomeTrustDivestmentRequestStatusEnum from '~Api/IncomeTrust/InvestorIncomeTrustDivestmentRequestStatusEnum';

export interface IInvestmentsState {
    dashboardUuids: string[];
    histories: IDictionary<IDictionary<IHistory>>;
    incomeTrustDistributions: IDictionary<IDistribution[]>;
    incomeTrustInterestRates: IDictionary<IIncomeTrustInterestRate>;
    incomeTrustTransactions: IDictionary<IIncomeTrustTransaction>;
    investmentInvestorAccountInvestmentUuids: IDictionary<string[]>;
    investmentReservationUuids: IDictionary<string[]>;
    investments: IDictionary<IInvestment>;
    investmentsListed: boolean;
    investmentsSearchResultUuids: string[];
    investmentTransactionInvestorAccountInvestmentTransactionUuids: IDictionary<string[]>;
    investmentTransactionUuids: IDictionary<string[]>;
    investorIncomeTrustDivestmentRequests: IDictionary<IInvestorIncomeTrustDivestmentRequest>;
    investorIncomeTrustDivestmentRequestsProcessInProgress: IDictionary<boolean>;
    investorIncomeTrustsDivestmentRequestsPendingUuids: string[];
    investorIncomeTrustTransactions: IDictionary<IInvestorIncomeTrustTransaction[]>;
    pendingDistributions: IDictionary<IPendingDistribution>;
    pendingIncomeTrustInvestmentRequests: IDictionary<IIncomeTrustInvestmentRequest>;
    photoInProgress: boolean;
    reservations: IDictionary<IReservation>;
    transactions: IDictionary<IInvestmentTransaction>;
}

const initialData: IInvestmentsState = {
    dashboardUuids: null,
    histories: {},
    incomeTrustDistributions: {},
    incomeTrustInterestRates: null,
    incomeTrustTransactions: null,
    investmentInvestorAccountInvestmentUuids: {},
    investmentReservationUuids: {},
    investmentTransactionInvestorAccountInvestmentTransactionUuids: {},
    investmentTransactionUuids: {},
    investments: null,
    investmentsListed: false,
    investmentsSearchResultUuids: null,
    investorIncomeTrustDivestmentRequests: {},
    investorIncomeTrustDivestmentRequestsProcessInProgress: {},
    investorIncomeTrustTransactions: {},
    investorIncomeTrustsDivestmentRequestsPendingUuids: null,
    pendingDistributions: null,
    pendingIncomeTrustInvestmentRequests: null,
    photoInProgress: false,
    reservations: {},
    transactions: {},
};

function statifyInvestorIncomeTrustDivestmentRequests(
    state: IInvestmentsState,
    investorIncomeTrustDivestmentRequests: IInvestorIncomeTrustDivestmentRequest[],
): IInvestmentsState {
    let clonedState: IInvestmentsState = { ...state };

    _.each(investorIncomeTrustDivestmentRequests, (investorIncomeTrustDivestmentRequest: IInvestorIncomeTrustDivestmentRequest): void => {
        clonedState = statifyInvestorIncomeTrustDivestmentRequest(clonedState, investorIncomeTrustDivestmentRequest);
    });

    return clonedState;
}

function statifyInvestorIncomeTrustDivestmentRequest(
    state: IInvestmentsState,
    investorIncomeTrustDivestmentRequest: IInvestorIncomeTrustDivestmentRequest,
): IInvestmentsState {
    return {
        ...state,
        investorIncomeTrustDivestmentRequests: {
            ...state.investorIncomeTrustDivestmentRequests,
            [investorIncomeTrustDivestmentRequest.uuid]: _.omit(investorIncomeTrustDivestmentRequest, 'investorAccountIncomeTrust'),
        },
    };
}

export function investmentsReducer(state: IInvestmentsState = initialData, action: AnyAction): IInvestmentsState {
    switch (action.type) {
        case InvestmentsActionsEnum.InvestmentIncomeTrustDistributionsSet: {
            const typedAction: IInvestmentIncomeTrustDistributionsSetAction = action as IInvestmentIncomeTrustDistributionsSetAction;

            return {
                ...state,
                incomeTrustDistributions: {
                    ...state.incomeTrustDistributions,
                    [typedAction.classType]: typedAction.distributions,
                },
            };
        }

        case InvestmentsActionsEnum.IncomeTrustInterestRatesSet: {
            const typedAction: IIncomeTrustInterestRatesSetAction = action as IIncomeTrustInterestRatesSetAction;

            return {
                ...state,
                incomeTrustInterestRates: _.keyBy(typedAction.interestRates, 'uuid'),
            };
        }

        case InvestmentsActionsEnum.InvestmentIncomeTrustInvestmentRequestCancel: {
            const typedAction: IInvestmentIncomeTrustInvestmentRequestCancelAction = action as IInvestmentIncomeTrustInvestmentRequestCancelAction;

            return {
                ...state,
                pendingIncomeTrustInvestmentRequests: _.omit(state.pendingIncomeTrustInvestmentRequests, typedAction.incomeTrustInvestmentRequestUuid),
            };
        }

        case InvestmentsActionsEnum.InvestmentIncomeTrustInvestmentRequestProcess: {
            const typedAction: IInvestmentIncomeTrustInvestmentRequestProcessAction = action as IInvestmentIncomeTrustInvestmentRequestProcessAction;

            return {
                ...state,
                pendingIncomeTrustInvestmentRequests: _.omit(state.pendingIncomeTrustInvestmentRequests, typedAction.incomeTrustInvestmentRequestUuid),
            };
        }

        case InvestmentsActionsEnum.InvestmentIncomeTrustInvestmentRequestsPendingSet: {
            const typedAction: IInvestmentIncomeTrustInvestmentRequestsPendingSetAction = action as IInvestmentIncomeTrustInvestmentRequestsPendingSetAction;

            return {
                ...state,
                pendingIncomeTrustInvestmentRequests: _.keyBy(typedAction.incomeTrustInvestmentRequests, 'uuid'),
            };
        }

        case InvestmentsActionsEnum.InvestorIncomeTrustDivestmentRequestSet: {
            const typedAction: IInvestorIncomeTrustDivestmentRequestSetAction = action as IInvestorIncomeTrustDivestmentRequestSetAction;

            return {
                ...state,
                ...statifyInvestorIncomeTrustDivestmentRequest(state, typedAction.investorIncomeTrustDivestmentRequest),
            };
        }

        case InvestmentsActionsEnum.InvestorIncomeTrustsDivestmentRequestsPendingClear: {
            return {
                ...state,
                investorIncomeTrustsDivestmentRequestsPendingUuids: null,
            };
        }

        case InvestmentsActionsEnum.InvestorIncomeTrustsDivestmentRequestsPendingSet: {
            const typedAction: IInvestorIncomeTrustsDivestmentRequestsPendingSetAction = action as IInvestorIncomeTrustsDivestmentRequestsPendingSetAction;

            return {
                ...state,
                ...statifyInvestorIncomeTrustDivestmentRequests(state, typedAction.investorIncomeTrustsDivestmentRequests),
                investorIncomeTrustsDivestmentRequestsPendingUuids: _.map(typedAction.investorIncomeTrustsDivestmentRequests, 'uuid'),
            };
        }

        case InvestmentsActionsEnum.InvestorIncomeTrustDivestmentRequestCancel: {
            const typedAction: IInvestorIncomeTrustDivestmentRequestCancelAction = action as IInvestorIncomeTrustDivestmentRequestCancelAction;

            return {
                ...state,
                investorIncomeTrustDivestmentRequests: {
                    ...state.investorIncomeTrustDivestmentRequests,
                    [typedAction.investorIncomeTrustDivestmentRequestUuid]: {
                        ...state.investorIncomeTrustDivestmentRequests[typedAction.investorIncomeTrustDivestmentRequestUuid],
                        status: InvestorIncomeTrustDivestmentRequestStatusEnum.Cancelled,
                    },
                },
            };
        }

        case InvestmentsActionsEnum.InvestorIncomeTrustDivestmentRequestProcessInProgressSet: {
            const typedAction: IInvestorIncomeTrustDivestmentRequestProcessInProgressSetAction = action as IInvestorIncomeTrustDivestmentRequestProcessInProgressSetAction;

            return {
                ...state,
                investorIncomeTrustDivestmentRequestsProcessInProgress: {
                    ...state.investorIncomeTrustDivestmentRequestsProcessInProgress,
                    [typedAction.investorIncomeTrustDivestmentRequestUuid]: typedAction.inProgress,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentIncomeTrustTransactionsSet: {
            const typedAction: IInvestmentIncomeTrustTransactionsSetAction = action as IInvestmentIncomeTrustTransactionsSetAction;

            return {
                ...state,
                incomeTrustTransactions: _.keyBy(typedAction.transactions, 'uuid'),
            };
        }

        case InvestmentsActionsEnum.InvestmentIncomeTrustTransactionInvestorsSet: {
            const typedAction: IInvestmentIncomeTrustTransactionInvestorsSetAction = action as IInvestmentIncomeTrustTransactionInvestorsSetAction;

            return {
                ...state,
                investorIncomeTrustTransactions: {
                    ...state.investorIncomeTrustTransactions,
                    [typedAction.incomeTrustTransactionUuid]: typedAction.transactions,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentHide: {
            const typedAction: IInvestmentHideAction = action as IInvestmentHideAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    [typedAction.uuid]: {
                        ...state.investments[typedAction.uuid],
                        workflowStatus: WorkflowStatusEnum.Ready,
                    },
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentPendingDistributionsSet: {
            const typedAction: IInvestmentPendingDistributionsSetAction = action as IInvestmentPendingDistributionsSetAction;

            const investments: IDictionary<IInvestment> = {};
            const pendingDistributions: IDictionary<IPendingDistribution> = {};

            typedAction.pendingDistributions.forEach((pendingDistribution: IPendingDistribution) => {
                investments[pendingDistribution.investment.uuid] = pendingDistribution.investment;
                pendingDistributions[pendingDistribution.warehouseLoanTransactionUuid] = _.omit(pendingDistribution, [ 'investment' ]);
            });

            return {
                ...state,
                investments: {
                    ...state.investments,
                    ...investments,
                },
                pendingDistributions,
            };
        }

        case InvestmentsActionsEnum.InvestmentPendingDistributionProcess: {
            const typedAction: IInvestmentPendingDistributionProcessAction = action as IInvestmentPendingDistributionProcessAction;

            return {
                ...state,
                pendingDistributions: _.omit(state.pendingDistributions, typedAction.warehouseLoanTransactionUuid),
            };
        }

        case InvestmentsActionsEnum.InvestmentPhotoInProgressSet: {
            const typedAction: IInvestmentPhotoInProgressSetAction = action as IInvestmentPhotoInProgressSetAction;

            return {
                ...state,
                photoInProgress: typedAction.inProgress,
            };
        }

        case InvestmentsActionsEnum.InvestmentReservationsSet: {
            const typedAction: IInvestmentReservationsSetAction = action as IInvestmentReservationsSetAction;

            const reservations: IDictionary<IReservation> = {};
            const reservationUuids: string[] = [];

            typedAction.reservations.forEach((reservation: IReservation) => {
                reservations[reservation.uuid] = reservation;
                reservationUuids.push(reservation.uuid);
            });

            return {
                ...state,
                investmentReservationUuids: {
                    ...state.investmentReservationUuids,
                    [typedAction.investmentUuid]: reservationUuids,
                },
                reservations: {
                    ...state.reservations,
                    ...reservations,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentSet: {
            const typedAction: IInvestmentSetAction = action as IInvestmentSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    [typedAction.investment.uuid]: typedAction.investment,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentHistoriesSet: {
            const typedAction: IInvestmentHistoriesSetAction = action as IInvestmentHistoriesSetAction;

            return {
                ...state,
                histories: {
                    ...state.histories,
                    [typedAction.uuid]: _.keyBy(typedAction.histories, 'uuid'),
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentTransactionsSet: {
            const typedAction: IInvestmentTransactionsSetAction = action as IInvestmentTransactionsSetAction;

            const investmentTransactionUuids: string[] = [];
            const transactions: IDictionary<IInvestmentTransaction> = {};

            typedAction.transactions.forEach((transaction: IInvestmentTransaction) => {
                investmentTransactionUuids.push(transaction.uuid);
                transactions[transaction.uuid] = transaction;
            });

            return {
                ...state,
                investmentTransactionUuids: {
                    ...state.investmentTransactionUuids,
                    [typedAction.investmentUuid]: investmentTransactionUuids,
                },
                transactions: {
                    ...state.transactions,
                    ...transactions,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentTransactionSet: {
            const typedAction: IInvestmentTransactionSetAction = action as IInvestmentTransactionSetAction;

            return {
                ...state,
                transactions: {
                    ...state.transactions,
                    [typedAction.transaction.uuid]: typedAction.transaction,
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentTransactionInvestorsSet: {
            const typedAction: IInvestmentTransactionInvestorsSetAction = action as IInvestmentTransactionInvestorsSetAction;

            return {
                ...state,
                investmentTransactionInvestorAccountInvestmentTransactionUuids: {
                    ...state.investmentTransactionInvestorAccountInvestmentTransactionUuids,
                    [typedAction.investmentTransactionUuid]: typedAction.transactions.map((transaction: IInvestorAccountInvestmentTransaction) => transaction.uuid),
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentValueSet: {
            const typedAction: IInvestmentValueSetAction = action as IInvestmentValueSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    [typedAction.uuid]: {
                        ...state.investments[typedAction.uuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentsDashboardSet: {
            const typedAction: IInvestmentsDashboardSetAction = action as IInvestmentsDashboardSetAction;

            return {
                ...state,
                dashboardUuids: _.map(typedAction.investments, (investment: IInvestment) => investment.uuid),
                investments: {
                    ...state.investments,
                    ..._.keyBy(typedAction.investments, 'uuid'),
                },
            };
        }

        case InvestmentsActionsEnum.InvestmentsSearchResultsSet: {
            const typedAction: IInvestmentsSearchResultsSetAction = action as IInvestmentsSearchResultsSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    ..._.keyBy(typedAction.investments, 'uuid'),
                },
                investmentsSearchResultUuids: _.map(typedAction.investments, (investment: IInvestment) => investment.uuid),
            };
        }

        case InvestmentsActionsEnum.InvestmentsSearchResultsClear: {
            return {
                ...state,
                investmentsSearchResultUuids: [],
            };
        }

        case InvestmentsActionsEnum.InvestmentsSet: {
            const typedAction: IInvestmentsSetAction = action as IInvestmentsSetAction;

            const investments: { [uuid: string]: IInvestment } = {};

            typedAction.investments.forEach((investment: IInvestment) => {
                investments[investment.uuid] = investment;
            });

            return {
                ...state,
                investments,
                investmentsListed: true,
            };
        }

        case InvestmentsActionsEnum.InvestmentInvestorsSet: {
            const typedAction: IInvestmentInvestorsSetAction = action as IInvestmentInvestorsSetAction;

            return {
                ...state,
                investmentInvestorAccountInvestmentUuids: {
                    ...state.investmentInvestorAccountInvestmentUuids,
                    [typedAction.investmentUuid]: _.map(typedAction.investorAccountInvestments, (investorAccountInvestment: IInvestorAccountInvestment) => investorAccountInvestment.uuid),
                },
            };
        }

        case InvestorsActionsEnum.InvestorAccountInvestmentSet: {
            const typedAction: IInvestorAccountInvestmentSetAction = action as IInvestorAccountInvestmentSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    [typedAction.investorAccountInvestment.investment.uuid]: typedAction.investorAccountInvestment.investment,
                },
            };
        }

        case InvestorsActionsEnum.InvestorAccountInvestmentsSet: {
            const typedAction: IInvestorAccountInvestmentsSetAction = action as IInvestorAccountInvestmentsSetAction;

            const investments: IDictionary<IInvestment> = { ...state.investments };

            typedAction.investorAccountInvestments.forEach((investorAccountInvestment: IInvestorAccountInvestment) => {
                investments[investorAccountInvestment.investment.uuid] = investorAccountInvestment.investment;
            });

            return {
                ...state,
                investments,
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransferSet: {
            const typedAction: IInvestmentSaleTransferSetAction = action as IInvestmentSaleTransferSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    [typedAction.investmentSaleTransfer.investmentUuid]: typedAction.investmentSaleTransfer.investment,
                },
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransfersUnallocatedInvestmentsSet: {
            const typedAction: IInvestmentSaleTransfersUnallocatedInvestmentsSetAction = action as IInvestmentSaleTransfersUnallocatedInvestmentsSetAction;

            const investments: IDictionary<IInvestment> = { ...state.investments };

            _.each(typedAction.unallocatedInvestments, (unallocatedInvestment: IUnallocatedInvestment) => {
                investments[unallocatedInvestment.investmentUuid] = unallocatedInvestment.investment;
            });

            return {
                ...state,
                investments,
            };
        }

        case LoansActionsEnum.LoanInvestmentsSet: {
            const typedAction: ILoanInvestmentsSetAction = action as ILoanInvestmentsSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    ..._.keyBy(typedAction.investments, 'uuid'),
                },
            };
        }

        case InvestmentSaleTransfersActionsEnum.InvestmentSaleTransfersPaginatedSet: {
            const typedAction: IInvestmentSaleTransfersPaginatedSetAction = action as IInvestmentSaleTransfersPaginatedSetAction;

            return {
                ...state,
                investments: {
                    ...state.investments,
                    ..._.reduce(typedAction.investmentSaleTransfers, (investmentsByUuid: IDictionary<IInvestment>, investmentSaleTransfer: IInvestmentSaleTransfer): IDictionary<IInvestment> => {
                        investmentsByUuid[investmentSaleTransfer.investment.uuid] = investmentSaleTransfer.investment;

                        return investmentsByUuid;
                    }, {}),
                },
            };
        }

        default:
            return state;
    }
}
